import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Fade,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConverterJobRunPanel from './ConverterJobRunPanel';
import ConverterTransformationSettingsForm from './ConverterTransformationSettingsForm';
import OptimizationPresetControls from './OptimizationPresetControls';
import {
  CONVERTER_JOB_PROFILE_S123_OPTIMIZATION,
  appendConverterJobTechnicalEntry,
  buildConverterJobDebugReport,
  downloadConverterJobDebugReport,
  normalizeConverterJobError,
} from './converter-job-diagnostics';
import {
  S123_OPTIMIZATION_ROUTE,
  TRANSFORMATION_PROFILES,
  buildTransformationSettings,
  createTransformationFormDefaults,
} from './converter-transformation-settings';
import {
  buildVariantOptimizationResultRoute,
  extractVariantOptimizationResultCalculationGuid,
  formatVariantOptimizationResultName,
  isVariantOptimizationAbortError,
  normalizeVariantOptimizationResultName,
  pollVariantOptimizationJob,
  runVariantOptimization,
} from './variant-optimization';
import { sendConversionAuditMessage } from './conversion-audit-message';

const VariantOptimizationModal = ({
  onClose,
  open,
  projectGuid,
  sourceCalculationGuid,
  sourceName,
}) => {
  const { t } = useTranslation();
  const activeControllerRef = useRef(null);
  const [clientError, setClientError] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [jobId, setJobId] = useState('');
  const [jobLifecycle, setJobLifecycle] = useState('idle');
  const [jobSourceCalculationGuid, setJobSourceCalculationGuid] = useState('');
  const [latestStatus, setLatestStatus] = useState(null);
  const [resultName, setResultName] = useState('');
  const [resultNameError, setResultNameError] = useState('');
  const [settings, setSettings] = useState(
    () => createTransformationFormDefaults(
      TRANSFORMATION_PROFILES.S123_OPTIMIZATION,
    ),
  );
  const [settingsErrors, setSettingsErrors] = useState({});
  const [statusText, setStatusText] = useState('');
  const [submittedSettings, setSubmittedSettings] = useState(null);
  const [technicalEntries, setTechnicalEntries] = useState([]);
  const [sessionSource, setSessionSource] = useState(() => ({
    sourceCalculationGuid,
    sourceName,
  }));
  const canResumeJob = (
    jobLifecycle === 'unresolved' &&
    jobId &&
    jobSourceCalculationGuid === sessionSource.sourceCalculationGuid
  );

  const handleSettingsChange = (nextSettings) => {
    setSettings(nextSettings);
    setSettingsErrors({});
  };

  const appendTechnicalEntry = (entry) => {
    setTechnicalEntries((currentEntries) => (
      appendConverterJobTechnicalEntry(currentEntries, entry)
    ));
  };

  const stopPolling = () => {
    activeControllerRef.current?.abort();
    activeControllerRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      stopPolling();
      return;
    }

    const nextSessionSource = {
      sourceCalculationGuid,
      sourceName,
    };
    setSessionSource(nextSessionSource);

    if (
      jobLifecycle === 'unresolved' &&
      jobId &&
      jobSourceCalculationGuid === nextSessionSource.sourceCalculationGuid
    ) {
      setIsRunning(false);
      return;
    }

    stopPolling();
    setClientError(null);
    setErrorMessage('');
    setIsRunning(false);
    setJobId('');
    setJobLifecycle('idle');
    setJobSourceCalculationGuid('');
    setLatestStatus(null);
    setResultName(
      formatVariantOptimizationResultName({
        sourceName:
          nextSessionSource.sourceName ||
          nextSessionSource.sourceCalculationGuid,
      }),
    );
    setResultNameError('');
    setSettings(
      createTransformationFormDefaults(
        TRANSFORMATION_PROFILES.S123_OPTIMIZATION,
      ),
    );
    setSettingsErrors({});
    setStatusText('');
    setSubmittedSettings(null);
    setTechnicalEntries([]);
  }, [open]);

  useEffect(() => () => {
    stopPolling();
  }, []);

  const downloadDebugReport = () => {
    const report = buildConverterJobDebugReport({
      error: clientError,
      jobId,
      latestStatus,
      profile: CONVERTER_JOB_PROFILE_S123_OPTIMIZATION,
      route: S123_OPTIMIZATION_ROUTE,
      sourceContext: {
        projectGuid,
        sourceCalculationGuid: sessionSource.sourceCalculationGuid,
        sourceName: sessionSource.sourceName || null,
      },
      submittedSettings,
      technicalEntries,
    });

    downloadConverterJobDebugReport({
      fileNameOptions: {
        jobId,
        profile: CONVERTER_JOB_PROFILE_S123_OPTIMIZATION,
        sourceCalculationGuid: sessionSource.sourceCalculationGuid,
        sourceName: sessionSource.sourceName,
      },
      report,
    });
  };

  const startOptimization = async () => {
    if (activeControllerRef.current !== null) {
      return;
    }

    const isResuming = Boolean(canResumeJob);
    let optimizationSettings = submittedSettings;
    let normalizedResultName = resultName;

    if (!isResuming) {
      try {
        normalizedResultName =
          normalizeVariantOptimizationResultName(resultName);
        setResultName(normalizedResultName);
        setResultNameError('');
      } catch {
        setResultNameError(
          t('variantOptimization.validation.resultName'),
        );
        return;
      }

      try {
        optimizationSettings = buildTransformationSettings({
          profile: TRANSFORMATION_PROFILES.S123_OPTIMIZATION,
          values: settings,
        });
      } catch (error) {
        setSettingsErrors(
          error?.field
            ? {
                [error.field]: t(
                  'converterTransformation.validation.invalidValue',
                ),
              }
            : {},
        );
        setErrorMessage(
          t('converterTransformation.validation.invalidSettings'),
        );
        return;
      }
    }

    const controller = new AbortController();
    let currentJobId = isResuming ? jobId : '';
    let currentLatestStatus = isResuming ? latestStatus : null;

    activeControllerRef.current = controller;
    setClientError(null);
    setErrorMessage('');
    setIsRunning(true);
    setSettingsErrors({});

    if (isResuming) {
      setStatusText(t('variantOptimization.messages.checkingStatus'));
      appendTechnicalEntry({
        event: 'job_status_polling_resumed',
        payload: { jobId: currentJobId },
      });
    } else {
      setJobId('');
      setJobLifecycle('idle');
      setJobSourceCalculationGuid('');
      setLatestStatus(null);
      setStatusText(t('variantOptimization.messages.creating'));
      setSubmittedSettings(optimizationSettings);
      setTechnicalEntries([]);
    }

    try {
      const pollingCallbacks = {
        onRetryableError: ({ attempt, error }) => {
          appendTechnicalEntry({
            event: 'job_status_retry_failed',
            payload: {
              attempt,
              error: normalizeConverterJobError(error),
            },
          });
        },
        onStatusChange: (currentJobStatus) => {
          currentLatestStatus = currentJobStatus;
          setLatestStatus(currentJobStatus);
          appendTechnicalEntry({
            event: 'job_status',
            payload: currentJobStatus,
          });

          if (currentJobStatus?.step) {
            setStatusText(
              `${currentJobStatus.status} · ${t(
                `variantOptimization.steps.${currentJobStatus.step}`,
                { defaultValue: currentJobStatus.step },
              )}`,
            );
            return;
          }

          setStatusText(
            currentJobStatus?.status === 'queued'
              ? t('variantOptimization.messages.queued')
              : currentJobStatus?.status || '',
          );
        },
        signal: controller.signal,
      };
      const jobStatus = isResuming
        ? await pollVariantOptimizationJob({
            ...pollingCallbacks,
            jobId: currentJobId,
          })
        : await runVariantOptimization({
            ...pollingCallbacks,
            onJobCreated: (createdJob) => {
              currentJobId = createdJob.jobId;
              setJobId(currentJobId);
              setJobLifecycle('unresolved');
              setJobSourceCalculationGuid(
                sessionSource.sourceCalculationGuid,
              );
              setStatusText(
                t('variantOptimization.messages.jobCreated', {
                  jobId: currentJobId,
                }),
              );
              appendTechnicalEntry({
                event: 'job_created',
                payload: createdJob,
              });
            },
            optimizationSettings,
            projectGuid,
            resultName: normalizedResultName,
            sourceCalculationGuid: sessionSource.sourceCalculationGuid,
          });

      if (controller.signal.aborted) {
        return;
      }

      if (jobStatus?.status === 'failed') {
        setJobLifecycle('failed');
        setErrorMessage(t('converterTransformation.job.failed'));
        return;
      }

      if (jobStatus?.status === 'publish_unknown') {
        setJobLifecycle('unresolved');
        setErrorMessage(t('converterTransformation.job.publishUnknown'));
        return;
      }

      const resultCalculationGuid =
        extractVariantOptimizationResultCalculationGuid(jobStatus);

      setJobLifecycle('completed');
      setStatusText(
        jobStatus?.outcome === 'no_changes'
          ? t('variantOptimization.messages.noChanges')
          : t('variantOptimization.messages.done'),
      );

      try {
        const conversionAuditMessageResponse = await sendConversionAuditMessage({
          calculationGuid: resultCalculationGuid,
          conversionAudit: jobStatus?.conversionAudit,
          projectGuid,
          signal: controller.signal,
        });

        appendTechnicalEntry({
          event: conversionAuditMessageResponse === undefined
            ? 'conversion_audit_message_missing'
            : 'conversion_audit_message_sent',
          payload: conversionAuditMessageResponse === undefined
            ? {
                jobId: currentJobId,
                status: jobStatus?.status,
              }
            : {
                calculationGuid: resultCalculationGuid,
                projectGuid,
                responseData: conversionAuditMessageResponse,
              },
        });
      } catch (error) {
        if (
          controller.signal.aborted ||
          isVariantOptimizationAbortError(error)
        ) {
          throw error;
        }

        appendTechnicalEntry({
          event: 'conversion_audit_message_failed',
          payload: normalizeConverterJobError(error),
        });
      }

      if (controller.signal.aborted) {
        return;
      }

      window.location.assign(
        buildVariantOptimizationResultRoute({
          jobStatus,
          projectGuid,
        }),
      );
    } catch (error) {
      if (
        controller.signal.aborted ||
        isVariantOptimizationAbortError(error)
      ) {
        return;
      }

      if (activeControllerRef.current !== controller) {
        return;
      }

      if (currentJobId) {
        setJobLifecycle('unresolved');
        setJobSourceCalculationGuid(sessionSource.sourceCalculationGuid);
      }
      setClientError(error);
      setErrorMessage(t('variantOptimization.messages.requestFailed'));
      appendTechnicalEntry({
        event: 'flow_failed',
        payload: {
          error: normalizeConverterJobError(error),
          jobId: currentJobId || null,
          latestStatus: currentLatestStatus,
        },
      });
    } finally {
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
        setIsRunning(false);
      }
    }
  };

  const handleClose = () => {
    stopPolling();
    setIsRunning(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 5,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 32px)',
            maxWidth: '100%',
            overflowY: 'auto',
            p: { xs: 2.5, sm: 4 },
            position: 'relative',
            width: { xs: 'calc(100vw - 32px)', sm: '720px' },
          }}
        >
          <IconButton
            aria-label={t('variantOptimization.buttons.close')}
            onClick={handleClose}
            sx={{ color: 'text.secondary', position: 'absolute', right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography component="h2" variant="h6" sx={{ pr: 5 }}>
            {t('variantOptimization.title')}
          </Typography>

          <Typography sx={{ mt: 2 }}>
            {t('variantOptimization.labels.variant')}:{' '}
            <strong>
              {sessionSource.sourceName ||
                sessionSource.sourceCalculationGuid}
            </strong>
          </Typography>

          <TextField
            disabled={isRunning || Boolean(canResumeJob)}
            error={Boolean(resultNameError)}
            fullWidth
            helperText={resultNameError || undefined}
            label={t('variantOptimization.labels.resultName')}
            onChange={(event) => {
              setResultName(event.target.value);
              setResultNameError('');
            }}
            sx={{ mt: 2 }}
            value={resultName}
          />

          <Typography color="warning.main" sx={{ mb: 3, mt: 2 }}>
            {t('variantOptimization.messages.savedDoorsWarning')}
          </Typography>

          <OptimizationPresetControls
            active={open}
            disabled={isRunning || Boolean(canResumeJob)}
            onChange={handleSettingsChange}
            profile={TRANSFORMATION_PROFILES.S123_OPTIMIZATION}
            value={settings}
          />

          <ConverterTransformationSettingsForm
            disabled={isRunning || Boolean(canResumeJob)}
            errors={settingsErrors}
            onChange={handleSettingsChange}
            profile={TRANSFORMATION_PROFILES.S123_OPTIMIZATION}
            value={settings}
          />

          <ConverterJobRunPanel
            errorMessage={errorMessage}
            isRunning={isRunning}
            labels={{
              downloadDebug: t('converterTransformation.job.downloadDebug'),
              progress: t('converterTransformation.job.status'),
              technicalDetails: t('converterTransformation.job.technicalLog'),
            }}
            onDownloadDebug={
              errorMessage && technicalEntries.length > 0
                ? downloadDebugReport
                : undefined
            }
            statusText={statusText}
            technicalEntries={technicalEntries}
          />

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 3,
            }}
          >
            <Button
              disabled={isRunning}
              onClick={startOptimization}
              variant="contained"
            >
              {t(
                canResumeJob
                  ? 'variantOptimization.buttons.checkStatus'
                  : 'variantOptimization.buttons.start',
              )}
            </Button>
            <Button onClick={handleClose} variant="outlined">
              {t('variantOptimization.buttons.close')}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default VariantOptimizationModal;
